import type { ReactNode } from 'react'

import { create } from 'zustand'

export type DialogVariant = 'default' | 'destructive' | 'info' | 'success' | 'warning'

type DialogBaseOptions = {
  title: string
  description?: string
  content?: ReactNode
  confirmLabel?: string
  variant?: DialogVariant
  /**
   * Quem enfileirou o diálogo. Existe só para o descarte seletivo: um fluxo que
   * recomeça limpa o que ele mesmo pediu (`clearDialogs(source)`) sem engolir a
   * pergunta que outro fluxo deixou aberta.
   */
  source?: string
}

export type ConfirmOptions = DialogBaseOptions & {
  cancelLabel?: string
  showCancel?: boolean
  confirmVariant?: 'default' | 'destructive'
}

export type AlertOptions = DialogBaseOptions

type ConfirmRequest = ConfirmOptions & {
  kind: 'confirm'
  resolve: (confirmed: boolean) => void
}

type AlertRequest = AlertOptions & {
  kind: 'alert'
  resolve: () => void
}

type DialogRequest = ConfirmRequest | AlertRequest

type DialogStore = {
  queue: DialogRequest[]
  current: DialogRequest | null
  enqueue: (request: DialogRequest) => void
  dequeue: () => void
  processNext: () => void
}

const useDialogStore = create<DialogStore>((set, get) => ({
  queue: [],
  current: null,
  enqueue: request => {
    const { current, queue } = get()
    if (!current) {
      set({ current: request })
      return
    }
    set({ queue: [...queue, request] })
  },
  dequeue: () => {
    const { queue } = get()
    const [next, ...rest] = queue
    set({ current: next ?? null, queue: rest })
  },
  processNext: () => {
    get().dequeue()
  }
}))

function enqueueConfirm(options: ConfirmOptions): Promise<boolean> {
  return new Promise(resolve => {
    useDialogStore.getState().enqueue({
      kind: 'confirm',
      confirmLabel: 'Confirmar',
      cancelLabel: 'Cancelar',
      showCancel: true,
      confirmVariant: 'default',
      ...options,
      resolve
    })
  })
}

function enqueueAlert(options: AlertOptions): Promise<void> {
  return new Promise(resolve => {
    useDialogStore.getState().enqueue({
      kind: 'alert',
      confirmLabel: 'OK',
      ...options,
      resolve
    })
  })
}

export function confirm(options: ConfirmOptions): Promise<boolean> {
  return enqueueConfirm(options)
}

export function alert(options: AlertOptions): Promise<void> {
  return enqueueAlert(options)
}

// Descarta diálogos pendentes, resolvendo o que remover.
//
// Serve a quem inicia uma intenção nova do participante — ler outro QR, por
// exemplo — e não quer que o diálogo da intenção anterior apareça no lugar do
// seu. Sem isso, a fila entrega o alerta velho primeiro e a pessoa lê a
// resposta de uma pergunta que já não é a dela.
//
// **Com `source`, descarta só o que aquela origem enfileirou.** É a forma de
// usar isto sem atropelar fluxo alheio: descartar a fila inteira resolveria um
// `confirm` de outro dono como `false` — um `showActiveSessionDialog` aberto
// viraria `'sign-out'` silencioso — e apagaria avisos que ninguém pediu para
// apagar. Sem `source` o comportamento antigo continua valendo, para quem
// realmente quer limpar tudo.
export function clearDialogs(source?: string): void {
  const { current, queue } = useDialogStore.getState()

  const isTarget = (request: DialogRequest) =>
    source === undefined || request.source === source

  const discard = (request: DialogRequest) =>
    request.kind === 'confirm' ? request.resolve(false) : request.resolve()

  const kept = queue.filter(request => {
    if (!isTarget(request)) return true

    discard(request)
    return false
  })

  if (current && isTarget(current)) {
    discard(current)

    const [next, ...rest] = kept
    useDialogStore.setState({ current: next ?? null, queue: rest })
    return
  }

  useDialogStore.setState({ current, queue: kept })
}

export function closeDialog(confirmed: boolean): void {
  const { current, processNext } = useDialogStore.getState()
  if (!current) return

  if (current.kind === 'confirm') {
    current.resolve(confirmed)
  } else {
    current.resolve()
  }

  processNext()
}

export { useDialogStore }

/**
 * `true` enquanto há dialog imperativo em tela. Quem exibe outra superfície
 * modal fora desta fila (o nudge da lojinha) usa isto como gate para não
 * sobrepor dois diálogos.
 */
export const useIsDialogOpen = () => useDialogStore(s => s.current !== null)
